// The card's aggregator: the period's scanned games that reached an ending,
// grouped by the kind of ending (`GameFacts.endingType`), each group split
// into the ending cases of "Your endings" (`GameFacts.endingCase`). The
// kinds are the books' material classes (`ENDING_TYPES` in
// `@chess/api-ending-case`): every kind that occurs gets a row, unless more
// than `MAX_ROWS` do, when the `MAX_ROWS - 1` with the most games keep theirs
// and the rest share "Other". Rows are ranked by the winning chances lost
// there (slipped, missed, lost on time), most first, then by games; "Other"
// comes last. Registered with the game
// store by this plugin's setup; the store runs it over the last 30 days'
// games on every change and keeps the result.
import { aggregatorId, collect, ENDING_CASES, type Accumulator, type Aggregator, type EndingCase, type EndingType, type GameFacts } from '@chess/api-model';
import { ENDING_TYPE_LABEL, ENDING_TYPES, LOST_CHANCES } from '@chess/api-ending-case';

/** The most rows the card shows: with more kinds than this, the rarest share "Other" (this many rows in all). */
export const MAX_ROWS = 8;

/** One bar: a kind of ending (or "Other", several), its games in each case. */
export interface EndingTypeRow {
  key: string;
  label: string;
  /** The kinds the bar covers: one, or several for "Other". */
  types: EndingType[];
  games: number;
  cases: Record<EndingCase, number>;
  /** Winning chances lost: slipped, missed and lost on time together. */
  lost: number;
}

/** What the card shows; plain data, so the card and its stories need no store. */
export interface EndingTypesResult {
  rows: EndingTypeRow[];
  /** Games that reached an ending, every bar together. */
  games: number;
}

const emptyCases = (): Record<EndingCase, number> => ({ won: 0, time: 0, slipped: 0, missed: 0, lost: 0, drawn: 0 });
const lostOf = (cases: Record<EndingCase, number>) => LOST_CHANCES.reduce((sum, c) => sum + cases[c], 0);

/** The bars for these games: one per kind (the commonest `MAX_ROWS - 1` when there are more than `MAX_ROWS`), ranked, then "Other". */
export function endingTypeRows(games: Iterable<GameFacts>): EndingTypesResult {
  const byType = new Map<EndingType, Record<EndingCase, number>>();
  let total = 0;
  for (const g of games) {
    if (!g.scanned || !g.endingCase || !g.endingType) continue;
    const cases = byType.get(g.endingType) ?? emptyCases();
    cases[g.endingCase] += 1;
    byType.set(g.endingType, cases);
    total += 1;
  }
  const all: EndingTypeRow[] = [];
  for (const type of ENDING_TYPES) {
    const cases = byType.get(type);
    if (!cases) continue;
    const games = ENDING_CASES.reduce((sum, c) => sum + cases[c], 0);
    all.push({ key: type, label: ENDING_TYPE_LABEL[type], types: [type], games, cases, lost: lostOf(cases) });
  }
  // Too many kinds: the commonest keep a row (ties to the most chances lost, then ENDING_TYPES' order; the sort is stable).
  const kept = all.length > MAX_ROWS ? new Set([...all].sort((a, b) => b.games - a.games || b.lost - a.lost).slice(0, MAX_ROWS - 1)) : new Set(all);
  const rows = all.filter((r) => kept.has(r));
  const other: EndingTypeRow = { key: 'other', label: 'Other', types: [], games: 0, cases: emptyCases(), lost: 0 };
  for (const r of all) {
    if (kept.has(r)) continue;
    other.types.push(...r.types);
    other.games += r.games;
    for (const c of ENDING_CASES) other.cases[c] += r.cases[c];
  }
  // Most winning chances lost first, then most games; ENDING_TYPES' order breaks the remaining ties (the sort is stable).
  rows.sort((a, b) => b.lost - a.lost || b.games - a.games);
  if (other.games) rows.push({ ...other, lost: lostOf(other.cases) });
  return { rows, games: total };
}

export class EndingTypesAggregator implements Aggregator<EndingTypesResult> {
  readonly id = aggregatorId('ending-types');

  start(): Accumulator<EndingTypesResult> {
    return collect((games) => endingTypeRows(games));
  }
}
